import { z } from 'zod';

// --- Paths ---
// Each path maps to a feature tour, launched on demand from the "?" help button
// on that feature's page (web header, mobile screen). Account setup for magic-link
// accounts with no sign-in method is the only remaining forced onboarding step.

export const ONBOARDING_PATHS = [
  'development',
  'research',
  'social',
  'vault'
] as const;

export type OnboardingPath = (typeof ONBOARDING_PATHS)[number];

export const ACCOUNT_SETUP_META = {
  title: 'Set up your account',
  description:
    'Choose a username and password to secure your account. You can always change these later in settings.'
};

// --- Device gate ---
// The one name for the device flag that says the activation funnel was skipped
// or completed here. Web keeps it in localStorage and mobile in AsyncStorage,
// under the same key, so every reader and writer imports it from this line.
export const SEEN_ACTIVATION_FUNNEL_KEY = 'seenActivationFunnel';

// --- Post-signup flow ---
// What a new account walks through right after signing up, as one flow with
// Next / Skip on both platforms: the bot-account verify prompt, the founders
// offer, then the "you're set" close that offers a recovery email.
export const POST_SIGNUP_STEPS = ['verify', 'founders', 'done'] as const;

export type PostSignupStep = (typeof POST_SIGNUP_STEPS)[number];

// A step with nothing to offer is left out of the sequence rather than shown
// empty, so the step count and "next" both skip it. Founders is the only such
// step: the token is not sold on every platform and can sell out.
export function postSignupSequence(
  hasFoundersOffer: boolean
): PostSignupStep[] {
  return POST_SIGNUP_STEPS.filter(
    (step) => step !== 'founders' || hasFoundersOffer
  );
}

// The step after `current` in that sequence, or null when `current` is the
// last one and the flow ends. A `current` the sequence left out (the founders
// step, asked for when there is no offer) resolves to the step that follows it.
export function nextPostSignupStep(
  current: PostSignupStep,
  hasFoundersOffer: boolean
): PostSignupStep | null {
  const sequence = postSignupSequence(hasFoundersOffer);
  const after = POST_SIGNUP_STEPS.slice(POST_SIGNUP_STEPS.indexOf(current) + 1);
  return after.find((step) => sequence.includes(step)) ?? null;
}

// --- Validators ---

// Client-reported tour funnel events. ACTIVATED is the per-path "aha"; meta
// carries the path, kept once per user+path.
export const ONBOARDING_CLIENT_EVENTS = [
  'TOUR_STEP',
  'TOUR_COMPLETED',
  'TOUR_SKIPPED',
  'ACTIVATED'
] as const;

export type OnboardingClientEvent = (typeof ONBOARDING_CLIENT_EVENTS)[number];

export const trackOnboardingEventSchema = z.object({
  type: z.enum(ONBOARDING_CLIENT_EVENTS),
  meta: z.string().max(500).optional(),
  // Device identifier: the funnel runs pre-auth, so events are keyed to the
  // installation rather than an account.
  installationId: z.string().max(64).optional()
});

// --- Helpers ---

export function shouldRequireOnboarding(user: {
  isAuthenticated: boolean | null;
  onboardingPath: string | null;
}): boolean {
  return user.isAuthenticated === true && !user.onboardingPath;
}
